import { Box } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useCallback, useContext, useEffect, useState } from 'react'
import { GeoJSON, LayersControl, MapContainer, TileLayer } from 'react-leaflet'
import AppContext from '../../context/appContext'
import { DCNamespaces } from '../../utils/dataCollection/namespaces'
import QUOverlay from './QUOverlay'

const DataCollectionMap = ({
  paddocksGeoJson,
  allPaddockIds,
  metricEvents,
  selectedMetricEvent,
  paddockProductionEvents,
  namespace,
  resetFormSignal,
  formMode,
  selectedPaddockIds,
  setAllSelected,
  usedPaddockIds,
  setUsedPaddockIds,
  setSelectedPaddockIds,
  quantificationUnits,
}) => {
  const [map, setMap] = useState()
  const [mapReady, setMapReady] = useState(false)
  const { programConfig } = useContext(AppContext)

  const featureUsed = feature => {
    return usedPaddockIds.includes(feature.properties.id)
  }

  const featureAlreadySelected = feature => {
    return selectedPaddockIds.includes(feature.properties.id)
  }

  const handleStyle = feature => {
    return {
      fillColor: featureUsed(feature) ? 'gray' : featureAlreadySelected(feature) ? 'red' : 'blue',
      color: featureUsed(feature) ? 'lightgray' : featureAlreadySelected(feature) ? 'red' : 'black',
      weight: 1,
      opacity: 1,
      fillOpacity: 0.5,
    }
  }

  const handleMouseOver = event => {
    const { layer } = event
    const tooltipContent = layer?.feature?.properties?.name
    layer.bindTooltip(tooltipContent).openTooltip()
  }

  const handleUpdateSelectedPaddocks = clickEvent => {
    const { feature } = clickEvent.layer
    if (!featureUsed(feature)) {
      let newSelected = []
      if (featureAlreadySelected(feature)) {
        // filter out feature and return the rest
        newSelected = selectedPaddockIds.filter(id => id !== feature.properties.id)
        setAllSelected(false)
      } else {
        // add feature to selected array
        newSelected = [...selectedPaddockIds, feature.properties.id]
        setAllSelected(newSelected.length === allPaddockIds.length - usedPaddockIds.length)
      }
      // call selection change
      setSelectedPaddockIds(newSelected)
    }
  }

  const resetPaddocksState = useCallback(() => {
    if (selectedMetricEvent != null && selectedMetricEvent.paddockIds) {
      setSelectedPaddockIds(selectedMetricEvent.paddockIds)
    } else {
      setSelectedPaddockIds([])
    }
    let newUsedPaddocks = []
    if (namespace !== DCNamespaces.grazing) {
      metricEvents.forEach(event => {
        if (
          event.paddockIds &&
          event.paddockIds.length > 0 &&
          event.id !== selectedMetricEvent?.id
        ) {
          newUsedPaddocks = newUsedPaddocks.concat(event.paddockIds)
        }
      })
    } else {
      paddockProductionEvents.forEach(event => {
        if (
          event.paddockIds &&
          event.paddockIds.length > 0 &&
          event.id !== selectedMetricEvent?.id &&
          event.namespace === DCNamespaces.paddocks &&
          !programConfig.allowedGrazingPaddockFieldUsages.includes(event.data.fieldUsageId)
        ) {
          newUsedPaddocks = newUsedPaddocks.concat(event.paddockIds)
        }
      })
    }
    setUsedPaddockIds(newUsedPaddocks)
  }, [
    selectedMetricEvent,
    namespace,
    setUsedPaddockIds,
    setSelectedPaddockIds,
    metricEvents,
    paddockProductionEvents,
    programConfig.allowedGrazingPaddockFieldUsages,
  ])

  useEffect(() => {
    if (!map || !paddocksGeoJson?.features?.length) return
    setMapReady(true)

    map.fitBounds(L.geoJSON(paddocksGeoJson).getBounds())
  }, [map, paddocksGeoJson])

  // Reset paddocks state when metricEvents data changes.
  useEffect(() => {
    resetPaddocksState()
  }, [metricEvents, resetPaddocksState, resetFormSignal])

  return (
    <Container disableGutters sx={{ width: '100%' }}>
      {!mapReady && (
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255,255,255,0.4)',
            zIndex: 9999,
            width: '-webkit-fill-available',
            height: '-webkit-fill-available',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <MapContainer
        ref={setMap}
        scrollWheelZoom
        zoomControl
        attributionControl={false}
        closePopupOnClick={false}
        doubleClickZoom={false}
        dragging={false}
        style={{ height: '75vh', width: '49vw' }}
        touchZoom={false}
        trackResize={false}
        wheelPxPerZoomLevel={60}
        zoom={5}
        zoomDelta={1}
        zoomSnap={0.1}
      >
        <TileLayer
          attribution=""
          subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
          url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
        />
        {paddocksGeoJson && (
          <GeoJSON
            data={paddocksGeoJson}
            eventHandlers={{
              click: (feature, layer) => {
                if (formMode.readOnly) return

                handleUpdateSelectedPaddocks(feature, layer)
              },
              mouseover: handleMouseOver,
            }}
            style={handleStyle}
          />
        )}
        {quantificationUnits?.length > 0 && (
          <LayersControl collapsed={false} position="topright">
            <LayersControl.Overlay name="Unidades de Cuantificación">
              <QUOverlay quantificationUnits={quantificationUnits} />
            </LayersControl.Overlay>
          </LayersControl>
        )}
      </MapContainer>
    </Container>
  )
}

export default DataCollectionMap
